import React, { useEffect, useState, useRef, useCallback } from "react";
import videojs from "video.js";
import "video.js/dist/video.js";
import "video.js/dist/video-js.css";
import "./FullscreenFeedPlayer.css";
import { getVideoMimeType, copyToClipboard } from "../helpers/globals";
import { getVideoUrl } from "../utils/urlHelpers";
import useVideoAnalytics from "../hooks/useVideoAnalytics";

const formatTime = (seconds) => {
  if (isNaN(seconds) || seconds < 0) return "0:00";
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs < 10 ? "0" : ""}${secs}`;
};

const FullscreenFeedPlayer = ({
  isOpen,
  onClose,
  videos,
  currentIndex,
  onIndexChange,
  isLiked,
  isDisliked,
  onLike,
  onDislike,
  isMutedGlobally,
  setIsMutedGlobally,
  playlistName,
}) => {
  const containerRef = useRef(null);
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const hideControlsTimerRef = useRef(null);
  const analytics = useVideoAnalytics();

  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isControlsVisible, setIsControlsVisible] = useState(true);
  const [showCenterIndicator, setShowCenterIndicator] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  const currentVideo = videos && videos[currentIndex] ? videos[currentIndex] : null;

  // Show a short toast message inside fullscreen
  const showToast = useCallback((msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(""), 2000);
  }, []);

  // Show and auto-hide controls on mouse movement
  const resetHideControlsTimer = useCallback(() => {
    setIsControlsVisible(true);
    if (hideControlsTimerRef.current) {
      clearTimeout(hideControlsTimerRef.current);
    }
    hideControlsTimerRef.current = setTimeout(() => {
      if (playerRef.current && !playerRef.current.paused()) {
        setIsControlsVisible(false);
      }
    }, 3000);
  }, []);

  // Request native fullscreen on mount/open
  useEffect(() => {
    if (!isOpen) return;

    const elem = containerRef.current;
    if (elem) {
      const requestFS =
        elem.requestFullscreen ||
        elem.webkitRequestFullscreen ||
        elem.mozRequestFullScreen ||
        elem.msRequestFullscreen;

      if (requestFS && !document.fullscreenElement) {
        requestFS.call(elem).catch((err) => {
          console.warn("Fullscreen request error:", err);
        });
      }
    }

    const handleFullscreenChange = () => {
      const fsElement =
        document.fullscreenElement ||
        document.webkitFullscreenElement ||
        document.mozFullScreenElement ||
        document.msFullscreenElement;

      if (!fsElement && isOpen) {
        onClose?.();
      }
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    document.addEventListener("webkitfullscreenchange", handleFullscreenChange);
    document.addEventListener("mozfullscreenchange", handleFullscreenChange);
    document.addEventListener("MSFullscreenChange", handleFullscreenChange);

    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
      document.removeEventListener("webkitfullscreenchange", handleFullscreenChange);
      document.removeEventListener("mozfullscreenchange", handleFullscreenChange);
      document.removeEventListener("MSFullscreenChange", handleFullscreenChange);
    };
  }, [isOpen, onClose]);

  // Advance to next video
  const handleNext = useCallback(() => {
    if (currentIndex < videos.length - 1) {
      onIndexChange(currentIndex + 1);
    }
  }, [currentIndex, videos.length, onIndexChange]);

  // Go to previous video
  const handlePrev = useCallback(() => {
    if (currentIndex > 0) {
      onIndexChange(currentIndex - 1);
    }
  }, [currentIndex, onIndexChange]);

  // Play/Pause toggle
  const togglePlayPause = useCallback(() => {
    if (!playerRef.current) return;
    if (playerRef.current.paused()) {
      playerRef.current.play().catch(console.error);
    } else {
      playerRef.current.pause();
    }
    setShowCenterIndicator(true);
    setTimeout(() => setShowCenterIndicator(false), 450);
  }, []);

  // Initialize player on first open
  useEffect(() => {
    if (!isOpen || !videoRef.current || playerRef.current) return;

    const videoOptions = {
      controls: false,
      autoplay: true,
      muted: isMutedGlobally,
      loop: false,
      preload: "auto",
      fluid: false,
    };

    const player = videojs(videoRef.current, videoOptions, function () {
      if (currentVideo?.videoUrl) {
        this.src({
          src: currentVideo.videoUrl,
          type: getVideoMimeType(currentVideo.videoUrl),
        });
        if (currentVideo.thumbnailUrl) {
          this.poster(currentVideo.thumbnailUrl);
        }
        this.currentTime(0);
        this.play().catch(console.error);
      }
    });

    player.on("play", () => {
      setIsPlaying(true);
      if (currentVideo) analytics.start(currentVideo.id);
    });

    player.on("pause", () => {
      setIsPlaying(false);
      analytics.pause();
    });

    player.on("timeupdate", () => {
      const cur = player.currentTime() || 0;
      const dur = player.duration() || 0;
      setCurrentTime(cur);
      setDuration(dur);
      if (dur > 0) {
        setProgress((cur / dur) * 100);
      }
    });

    player.on("ended", () => {
      setIsPlaying(false);
      analytics.record();
      // Auto-advance to next video (YouTube style)
      setTimeout(() => {
        handleNext();
      }, 300);
    });

    playerRef.current = player;

    return () => {
      if (playerRef.current) {
        playerRef.current.dispose();
        playerRef.current = null;
      }
    };
  }, [isOpen]); // Only initialize once on mount/open

  // In-place source swapping when currentVideo changes (YouTube behavior)
  useEffect(() => {
    if (!isOpen || !playerRef.current || !currentVideo?.videoUrl) return;

    analytics.record();
    const player = playerRef.current;
    player.pause();
    player.src({
      src: currentVideo.videoUrl,
      type: getVideoMimeType(currentVideo.videoUrl),
    });
    if (currentVideo.thumbnailUrl) {
      player.poster(currentVideo.thumbnailUrl);
    }
    player.currentTime(0);
    setProgress(0);
    setCurrentTime(0);
    player.play().catch(console.error);
  }, [currentVideo?.id, isOpen]);

  // Synchronize mute state
  useEffect(() => {
    if (playerRef.current) {
      playerRef.current.muted(isMutedGlobally);
    }
  }, [isMutedGlobally]);

  // Handle Seek
  const handleSeek = (e) => {
    if (!playerRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, clickX / rect.width));
    const dur = playerRef.current.duration() || 0;
    if (dur > 0) {
      const newTime = percentage * dur;
      playerRef.current.currentTime(newTime);
      setCurrentTime(newTime);
      setProgress(percentage * 100);
    }
  };

  // Keyboard navigation inside fullscreen
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;

      if (e.key === "ArrowDown" || e.key === "PageDown" || e.key === "j" || e.key === "J" || e.key === "n" || e.key === "N") {
        e.preventDefault();
        handleNext();
      } else if (e.key === "ArrowUp" || e.key === "PageUp" || e.key === "k" || e.key === "K" || e.key === "p" || e.key === "P") {
        e.preventDefault();
        handlePrev();
      } else if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        togglePlayPause();
      } else if (e.key === "m" || e.key === "M") {
        e.preventDefault();
        setIsMutedGlobally((prev) => !prev);
      } else if (e.key === "f" || e.key === "F" || e.key === "Escape") {
        e.preventDefault();
        onClose();
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        if (playerRef.current) {
          const cur = playerRef.current.currentTime();
          playerRef.current.currentTime(Math.max(0, cur - 5));
        }
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        if (playerRef.current) {
          const cur = playerRef.current.currentTime();
          const dur = playerRef.current.duration() || 0;
          playerRef.current.currentTime(Math.min(dur, cur + 5));
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, handleNext, handlePrev, togglePlayPause, setIsMutedGlobally, onClose]);

  // Handle Share link
  const handleCopyLink = (e) => {
    e.stopPropagation();
    if (!currentVideo) return;
    const videoUrl = `${window.location.protocol}//${window.location.host}${getVideoUrl(currentVideo)}`;
    copyToClipboard(videoUrl);
    showToast("Copied");
  };

  if (!isOpen || !currentVideo) return null;

  return (
    <div
      ref={containerRef}
      className={`fullscreen-feed-player ${!isControlsVisible ? "cursor-hidden" : ""}`}
      onMouseMove={resetHideControlsTimer}
      onClick={resetHideControlsTimer}
    >
      {/* Toast Notification */}
      {toastMessage && <div className="fullscreen-toast">{toastMessage}</div>}

      {/* Video Tech Wrapper */}
      <div className="fullscreen-video-wrapper" onClick={togglePlayPause}>
        <video
          ref={videoRef}
          className="video-js fullscreen-video-player"
          playsInline
        />

        {/* Center Play/Pause Flash Indicator */}
        {showCenterIndicator && (
          <div className="fullscreen-center-indicator">
            {isPlaying ? (
              <svg viewBox="0 0 24 24" width="36" height="36" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="36" height="36" fill="currentColor">
                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            )}
          </div>
        )}
      </div>

      {/* Overlay Chrome */}
      <div className="fullscreen-player-overlay">
        {/* Top Bar */}
        <div className="fullscreen-top-bar">
          <div className="fullscreen-top-left">
            <button
              className="fullscreen-back-btn"
              onClick={(e) => {
                e.stopPropagation();
                onClose();
              }}
              title="Exit Fullscreen (Esc)"
            >
              <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2.5">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <div className="fullscreen-top-info">
              <span className="fullscreen-playlist-name">{playlistName || "Music Video Feed"}</span>
              <h2 className="fullscreen-track-title">{currentVideo.title}</h2>
            </div>
          </div>

          <div className="fullscreen-top-right">
            <div className="fullscreen-counter-badge">
              {currentIndex + 1} / {videos.length}
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="fullscreen-bottom-bar" onClick={(e) => e.stopPropagation()}>
          {/* Progress Bar / Scrubber */}
          <div className="fullscreen-progress-container" onClick={handleSeek}>
            <div className="fullscreen-progress-track">
              <div
                className="fullscreen-progress-fill"
                style={{ width: `${progress}%` }}
              >
                <div className="fullscreen-progress-handle" />
              </div>
            </div>
          </div>

          {/* Controls Row */}
          <div className="fullscreen-controls-row">
            {/* Left Controls */}
            <div className="fullscreen-controls-left">
              <button
                className="fullscreen-ctrl-btn"
                onClick={handlePrev}
                disabled={currentIndex === 0}
                title="Previous Video (Up Arrow)"
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                  <path d="M6 6h2v12H6zm3.5 6l8.5 6V6z" />
                </svg>
              </button>

              <button
                className="fullscreen-ctrl-btn"
                onClick={togglePlayPause}
                title={isPlaying ? "Pause (Space)" : "Play (Space)"}
              >
                {isPlaying ? (
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                    <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="28" height="28" fill="currentColor">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                )}
              </button>

              <button
                className="fullscreen-ctrl-btn"
                onClick={handleNext}
                disabled={currentIndex === videos.length - 1}
                title="Next Video (Down Arrow)"
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                  <path d="M6 18l8.5-6L6 6v12zM16 6v12h2V6h-2z" />
                </svg>
              </button>

              <button
                className="fullscreen-ctrl-btn"
                onClick={() => setIsMutedGlobally((prev) => !prev)}
                title={isMutedGlobally ? "Unmute (M)" : "Mute (M)"}
              >
                {isMutedGlobally ? (
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <line x1="23" y1="9" x2="17" y2="15" />
                    <line x1="17" y1="9" x2="23" y2="15" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" strokeWidth="2">
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" />
                  </svg>
                )}
              </button>

              <div className="fullscreen-time-display">
                {formatTime(currentTime)} / {formatTime(duration)}
              </div>
            </div>

            {/* Center Info */}
            <div className="fullscreen-controls-center">
              <p className="fullscreen-center-title">{currentVideo.title}</p>
              <p className="fullscreen-center-artist">{currentVideo.artist}</p>
            </div>

            {/* Right Controls */}
            <div className="fullscreen-controls-right">
              {/* Star / Save to Library */}
              <button
                className={`fullscreen-action-btn ${isLiked ? "active" : ""}`}
                onClick={() => {
                  onLike(currentVideo.id);
                  showToast(isLiked ? "Removed from Library" : "Saved to Library ⭐");
                }}
                title="Save to library"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill={isLiked ? "currentColor" : "none"} stroke="currentColor" strokeWidth="2">
                  <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                </svg>
              </button>

              {/* Share */}
              <button
                className="fullscreen-action-btn"
                onClick={handleCopyLink}
                title="Copy video link"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                  <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                </svg>
              </button>

              {/* Dislike */}
              <button
                className={`fullscreen-action-btn ${isDisliked ? "active" : ""}`}
                onClick={() => {
                  onDislike(currentVideo.id);
                  showToast("Marked as Not for me");
                  setTimeout(() => handleNext(), 600);
                }}
                title="Not for me"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10 15v4a3 3 0 0 0 3 3l4-9V2H5.72a2 2 0 0 0-2 1.7l-1.38 9a2 2 0 0 0 2 2.3zm7-13h2.67A2.31 2.31 0 0 1 22 4v7a2.31 2.31 0 0 1-2.33 2H17" />
                </svg>
              </button>

              {/* Exit Fullscreen */}
              <button
                className="fullscreen-action-btn"
                onClick={onClose}
                title="Exit Fullscreen (Esc / F)"
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M8 3v3a2 2 0 0 1-2 2H3m18 0h-3a2 2 0 0 1-2-2V3m0 18v-3a2 2 0 0 1 2-2h3M3 16h3a2 2 0 0 1 2 2v3" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FullscreenFeedPlayer;
